import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { PageHeader } from '../components/PageHeader';

/** GET /v1/registry/company — domain `COMPANY` sabitinin alanları. */
interface CompanyInfo {
  name: string;
  shortName: string;
  address: string;
  web: string;
  email: string;
  phone: string;
  qmsRevision: string;
  qmsRevisionDate: string;
  certifications: string[];
  excludedClauses: string[];
}

interface CoverageSummary {
  totalProcedures: number;
  byAutomation: Record<string, number>;
  documents?: { total: number; byCategory: Record<string, number> };
}

interface HealthCheck {
  ok: boolean;
  detail?: string;
}

/** GET /v1/agent/health — 200 yalnız bütün kontroller geçerse; 503 gövdesi de aynı biçimde. */
interface AgentHealth {
  ok: boolean;
  error?: string;
  models?: string[];
  checks?: {
    ollama: HealthCheck;
    chatModel: HealthCheck & { name?: string };
    embedding: HealthCheck & { name?: string; dim?: number };
    collection: HealthCheck & { name?: string; pointsCount?: number; vectorSize?: number };
    dimension: HealthCheck;
  };
}

function agentHealthHint(h: AgentHealth): string {
  const c = h.checks;
  if (!c) return `${h.models?.length ?? 0} model`;
  return `${c.chatModel.name ?? '—'} · ${c.embedding.dim ?? '—'} boyut · ${c.collection.pointsCount ?? '—'} parça`;
}

function agentHealthErrorHint(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as Partial<AgentHealth> | null;
    if (body && typeof body.error === 'string') return body.error;
    return err.message;
  }
  return err ? String(err) : '';
}

interface RagStatus {
  collection: string;
  embedModel: string;
  documents: number;
  indexed: boolean;
  chunks: number | null;
  vectorSize: number | null;
  status: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  'el-kitabi': 'El Kitabı',
  prosedur: 'Prosedür',
  politika: 'Politika',
  talimat: 'Talimat',
  form: 'Form',
  'surec-karti': 'Süreç Kartı',
  diyagram: 'Diyagram',
  'gorev-tanimi': 'Görev Tanımı',
  dokuman: 'Diğer',
};
const CATEGORY_ORDER = [
  'el-kitabi',
  'prosedur',
  'politika',
  'talimat',
  'form',
  'surec-karti',
  'diyagram',
  'gorev-tanimi',
  'dokuman',
];

export function DashboardPage() {
  const company = useQuery<CompanyInfo>({
    queryKey: ['registry', 'company'],
    queryFn: () => api.get<CompanyInfo>('/v1/registry/company'),
  });

  const coverage = useQuery<CoverageSummary>({
    queryKey: ['registry', 'coverage'],
    queryFn: () => api.get<CoverageSummary>('/v1/registry/coverage'),
  });

  const agentHealth = useQuery<AgentHealth>({
    queryKey: ['agent', 'health'],
    queryFn: () => api.get<AgentHealth>('/v1/agent/health'),
    retry: false,
  });

  const rag = useQuery<RagStatus>({
    queryKey: ['registry', 'rag-status'],
    queryFn: () => api.get<RagStatus>('/v1/registry/rag-status'),
    retry: false,
  });

  const byCat = coverage.data?.documents?.byCategory ?? {};
  const docTotal = coverage.data?.documents?.total;

  return (
    <div>
      <PageHeader
        title="Kalite Yönetim Paneli"
        subtitle={
          company.data
            ? `${company.data.name} · ${company.data.qmsRevision}`
            : 'KYS özet görünümü'
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          title="Kontrollü Doküman"
          value={docTotal ?? '—'}
          hint={
            coverage.data?.documents
              ? `${byCat.prosedur ?? 0} prosedür · ${byCat.form ?? 0} form · ${byCat.talimat ?? 0} talimat`
              : 'Güncel Doküman Listesi'
          }
        />
        <Card
          title="Prosedür"
          value={coverage.data?.totalProcedures ?? '—'}
          hint={`SE-PR-01 → SE-PR-46 · ${coverage.data?.byAutomation?.FULL ?? 0} tam otomatik`}
        />
        <Card
          title="RAG İndeksi"
          value={rag.data?.indexed ? `${rag.data.chunks ?? '—'} chunk` : rag.isError ? 'Erişilemez' : '—'}
          hint={
            rag.data
              ? `${rag.data.documents} doküman · ${rag.data.embedModel}`
              : 'Qdrant se_qms_docs'
          }
          tone={rag.data?.indexed ? 'ok' : rag.isError ? 'warn' : 'neutral'}
        />
        <Card
          title="Agent Durumu"
          value={agentHealth.isError ? 'Sorunlu' : agentHealth.data?.ok ? 'Hazır' : 'Kontrol…'}
          hint={agentHealth.isError ? agentHealthErrorHint(agentHealth.error) : agentHealth.data ? agentHealthHint(agentHealth.data) : ''}
          tone={agentHealth.isError ? 'warn' : agentHealth.data?.ok ? 'ok' : 'neutral'}
        />
      </div>

      {coverage.data?.documents && (
        <div className="card mt-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Doküman Dağılımı
            </h2>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {company.data?.excludedClauses?.length
                ? `Hariç madde: ${company.data.excludedClauses.join(', ')}`
                : ''}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {CATEGORY_ORDER.filter((k) => byCat[k]).map((k) => (
              <div
                key={k}
                className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800"
              >
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {byCat[k]}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {CATEGORY_LABEL[k] ?? k}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            AS9100D Maddeleri
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            46 prosedürün AS9100D maddelerine eşlemesi.
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            {[
              ['§6.1, §8.1.1', 'Risk yönetimi'],
              ['§8.4', 'Tedarikçi performansı'],
              ['§8.5.1.3, §8.6.3', 'AS9102 FAIR'],
              ['§8.7', 'Uygunsuzluk kontrolü'],
              ['§9.1.1, §9.1.3', 'KPI / izleme'],
              ['§9.2', 'İç tetkik'],
              ['§9.3', 'YGG'],
              ['§10.2', 'CAPA'],
            ].map(([clause, label]) => (
              <li
                key={clause}
                className="flex items-center justify-between rounded-md px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-800/60"
              >
                <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                  {clause}
                </span>
                <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Hızlı İşlemler
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            En sık kullanılan akışlar.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <QuickLink to="/agent" title="KYS Agent" subtitle="Soru sor, kanıt al" />
            <QuickLink to="/reports" title="Rapor Üret" subtitle="NCR / KPI / FAIR" />
            <QuickLink to="/capa" title="CAPA Aç" subtitle="Düzeltici faaliyet" />
            <QuickLink
              to="/registry"
              title="Doküman Listesi"
              subtitle={docTotal ? `${docTotal} kontrollü doküman` : 'Tüm dokümanlar'}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({
  title,
  value,
  hint,
  tone = 'neutral',
}: {
  title: string;
  value: string | number;
  hint?: string;
  tone?: 'ok' | 'warn' | 'neutral';
}) {
  const tones: Record<string, string> = {
    ok: 'bg-emerald-50 ring-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:ring-emerald-900 dark:text-emerald-200',
    warn: 'bg-amber-50 ring-amber-200 text-amber-900 dark:bg-amber-950/40 dark:ring-amber-900 dark:text-amber-200',
    neutral:
      'bg-white ring-slate-200 text-slate-900 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100',
  };
  return (
    <div className={`rounded-lg p-4 ring-1 ${tones[tone]}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-70">{title}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs opacity-70">{hint}</div>}
    </div>
  );
}

function QuickLink({ to, title, subtitle }: { to: string; title: string; subtitle: string }) {
  return (
    <Link
      to={to}
      className="rounded-md border border-slate-200 px-3 py-2 transition hover:border-brand-300 hover:bg-brand-50 dark:border-slate-700 dark:hover:border-brand-700 dark:hover:bg-brand-900/30"
    >
      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{title}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</div>
    </Link>
  );
}
